import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { SelectDropdown } from '../SelectDropdown';

export const BudgetLineItemModal = ({
    isOpen,
    onClose,
    options,
    categoryId,
    onAccept
}) => {

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedType, setSelectedType] = useState("EDITABLE");
    const [defaultMonthValue, setDefaultMonthValue] = useState(0);
    const [accountNumber, setAccountNumber] = useState("");
    const [accountName, setAccountName] = useState("");

    useEffect(() => {
        if (!isOpen) return;
        if (categoryId) {
            setSelectedCategory(categoryId);
        }
        setAccountNumber("");
        setAccountName("");
        setDefaultMonthValue(0);
    }, [isOpen]);

    const handleAcceptActions = async () => {
        if (!accountName && !accountNumber) {
            toast.error(`Empty budget line item options.`, { position: "bottom-right" });
            return;
        }
        await onAccept?.({
            accountName,
            accountNumber,
            selectedType,
            selectedCategory,
            defaultMonthValue: defaultMonthValue || null
        });
    }

    const handleClose = () => {
        onClose();
    };

    // Handle Esc key press
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                    aria-hidden="true"
                    onClick={handleClose}
                ></div>
                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
                <div 
                    className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="bg-blue-50 px-6 py-4 border-b border-blue-200 rounded-t-lg">
                        <h2 className="text-xl font-semibold text-blue-900 text-left">Add New Budget Line Item</h2>
                        <p className="text-sm text-blue-700 mt-1 text-left">Enter the details for the new budget account</p>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700 text-left" htmlFor='account-number'>
                                    Account Number *
                                </label>
                                <input
                                    id='account-number'
                                    className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                                    placeholder="e.g., 50800"
                                    value={accountNumber}
                                    onChange={(e) => {
                                        setAccountNumber(e.target.value)
                                    }}
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700 text-left" htmlFor='account-name'>
                                    Account Name *
                                </label>
                                <input
                                    id='account-name'
                                    className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                                    placeholder="e.g., New Expense Item"
                                    value={accountName}
                                    onChange={(e) => {
                                        setAccountName(e.target.value)
                                    }}
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700 text-left">
                                    Category
                                </label>
                                <div>
                                    <SelectDropdown
                                        key={`${categoryId}_${selectedCategory}`}
                                        options={options}
                                        value={selectedCategory}
                                        onChange={setSelectedCategory}
                                        itemValueClassname="px-2 py-1 text-sm text-left"
                                        selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
                                        selectClassname="!bg-white !px-3 !py-2 !border-input"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700 text-left">
                                    Type
                                </label>
                                <div>
                                    <SelectDropdown
                                        key={`${categoryId}_${selectedCategory}`}
                                        options={[
                                            { label: "Editable", value: "EDITABLE" },
                                            { label: "Calculated", value: "CALCULATED" },
                                        ]}
                                        value={selectedType}
                                        onChange={setSelectedType}
                                        itemValueClassname="px-2 py-1 text-sm text-left"
                                        selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
                                        selectClassname="!bg-white !px-3 !py-2 !border-input"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700 text-left" htmlFor='default-value'>
                                    Default Monthly Value
                                </label>
                                <div>
                                    <input
                                        id='default-value'
                                        className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                                        type="number"
                                        min="0"
                                        value={defaultMonthValue}
                                        onChange={(e) => {
                                            let rawValue = e.target.value;
                                            let normalizedValue = rawValue.replace(',', '.');
                                            if (/^\d*\.?\d*$/.test(normalizedValue)) {
                                                if (
                                                    normalizedValue.includes('.') &&
                                                    normalizedValue.startsWith('0') &&
                                                    !normalizedValue.startsWith('0.')
                                                ) {
                                                    normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
                                                } else if (!normalizedValue.includes('.') && normalizedValue.length > 1) {
                                                    normalizedValue = normalizedValue.replace(/^0+/, '') || '0';
                                                }
                                                setDefaultMonthValue(normalizedValue)
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 border h-10 px-4 py-2 hover:bg-gray-100"
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="ring-offset-background inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors h-10 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={handleAcceptActions}
                            >
                                Add Line Item
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};